<template>
    <span v-if="id">
        <b-button
            class="align-baseline mr-2"
            title="View"
            @click="handleViewClick"
            variant="link"
            v-if="isOwnerStatement"
        >
            <font-awesome-icon :icon="['fas', 'eye']"></font-awesome-icon>
        </b-button>
        <b-button
            class="align-baseline mr-2"
            title="Regenerate"
            v-b-modal="regenerateModalId"
            variant="link"
            :disabled="actionButtonsDisabled"
        >
            <font-awesome-icon :icon="['fas', 'rotate']"></font-awesome-icon>
        </b-button>
        <b-button
            class="align-baseline mr-2"
            v-if="isOwnerStatement"
            :title="isPublished ? 'Unpublish' : 'Publish'"
            v-b-modal="publishModalId"
            variant="link"
        >
            <font-awesome-icon :icon="['fas', isPublished ? 'xmark' : 'check']"></font-awesome-icon>
        </b-button>
        <b-button
            class="align-baseline mr-2"
            v-if="!isOwnerStatement"
            title="Logs"
            @click="handleLogsClick"
            variant="link"
        >
            <font-awesome-icon :icon="['far', 'fa-file-lines']"></font-awesome-icon>
        </b-button>
        <b-button
            class="align-baseline"
            title="Delete"
            v-b-modal="deleteModalId"
            variant="link"
            :disabled="actionButtonsDisabled"
        >
            <font-awesome-icon :icon="['fas', 'trash']"></font-awesome-icon>
        </b-button>
        <modal
            :id="deleteModalId"
            :title="`Delete Owner Statement${statementPostfix}`"
            ok-title="Delete"
            :ok-disabled="loading"
            @ok.prevent="deleteOwnerStatement"
        >
            <busy-modal :show="loading"></busy-modal>
            Are you sure you want to delete {{ statementPrefix }} Owner Statement{{ statementPostfix }}?
        </modal>
        <owner-statement-publish-unpublish-modal
            :modal-id="publishModalId"
            :action="isPublished ? 'unpublish' : 'publish'"
            :owners-data="[{ statementId: this.id }]"
            :is-single-owner-selected="!!this.ownerId"
            :is-bulk-action="false"
            :loading="loading"
            :statement-prefix="statementPrefix"
            :statement-postfix="statementPostfix"
            @ok="publishUnpublish"
        />
        <modal
            :id="regenerateModalId"
            :title="`Regenerate Owner Statement${statementPostfix}`"
            ok-title="Regenerate"
            :ok-disabled="loading"
            @ok.prevent="regenerate"
        >
            <busy-modal :show="loading"></busy-modal>
            Are you sure you want to regenerate {{ statementPrefix }} Owner Statement{{ statementPostfix }}?
        </modal>
    </span>
</template>

<script>
    import API from '../../api';
    import Modal from '../generic/modal/modal.vue';
    import BusyModal from '../generic/busy-modal/busy-modal.vue';
    import FormInput from '../generic/form-input/form-input.vue';
    import FormTextarea from '../generic/form-textarea/form-textarea.vue';
    import FormCheckbox from '../generic/form-checkbox/form-checkbox.vue';
    import FormDatepicker from '../generic/form-datepicker/form-datepicker.vue';
    import FormSwitch from '../generic/form-switch/form-switch.vue';
    import OwnerStatementPublishUnpublishModal from './owner-statement-publish-unpublish-modal.vue';
    import { formatResponseError } from '../../utils/api';
    import { OWNER_STATEMENT_GENERATION_ENDPOINT, OWNER_STATEMENT_ENDPOINT } from '../../endpoints';

    export default {
        components: {
            Modal,
            BusyModal,
            FormInput,
            FormTextarea,
            FormCheckbox,
            FormDatepicker,
            FormSwitch,
            OwnerStatementPublishUnpublishModal,
        },
        data() {
            return {
                loading: false,
                created: null,
                status: '',
                id: null,
                deleteModalId: '',
                publishModalId: '',
                regenerateModalId: '',
            };
        },
        created() {
            this.id = this.params.data && this.params.data.id;
            this.status = this.params.data && this.params.data.status;
            this.created = this.params.data && this.params.data.created;
            this.publishedCount = this.params.data && this.params.data.published_count;
            this.statementsCount = this.params.data && this.params.data.statements_count;
            this.ownerId = this.params.data && this.params.data.owner_id;
            this.deleteModalId = `modal-delete-${this.created}`;
            this.publishModalId = `modal-publish-${this.created}`;
            this.regenerateModalId = `modal-regenerate-${this.created}`;
        },
        computed: {
            isPublished() {
                return this.status === 'Published';
            },
            isOwnerStatement() {
                return !!this.status;
            },
            statementPrefix() {
                return this.statementsCount && this.statementsCount > 1 ? 'these' : 'this';
            },
            statementPostfix() {
                return this.statementsCount && this.statementsCount > 1 ? 's' : '';
            },
            actionButtonsDisabled() {
                if (this.isOwnerStatement) return this.isPublished;
                return this.publishedCount > 0;
            },
        },
        methods: {
            async deleteOwnerStatement() {
                this.loading = true;
                try {
                    if (this.isOwnerStatement) {
                        await API.delete(OWNER_STATEMENT_ENDPOINT(this.id));
                        this.params.onDeleteSuccess && this.params.onDeleteSuccess(this.id);
                    } else {
                        await API.delete(OWNER_STATEMENT_GENERATION_ENDPOINT(this.id));
                        this.params.onDeleteSuccess(this.statementPostfix, this.id);
                    }
                } catch (err) {
                    const { generic, fields } = formatResponseError(err);
                    this.errorToast({ text: generic || `Could not delete owner statement${this.statementPostfix}.` });
                } finally {
                    this.loading = false;
                    this.$bvModal.hide(this.deleteModalId);
                }
            },
            async publishUnpublish(params) {
                this.loading = true;
                try {
                    const payload = this.isPublished
                        ? {}
                        : {
                              save_to_owner_account: params.saveToOwnerAccount,
                              send_email_to_owner: params.sendEmailToOwner,
                              email_data: params.emailData,
                          };
                    const { data } = await API.post(
                        OWNER_STATEMENT_ENDPOINT(this.id) + `${this.isPublished ? 'unpublish/' : 'publish/'}`,
                        payload
                    );
                    this.isPublished ? this.params.onUnpublishSuccess(data) : this.params.onPublishSuccess(data);
                } catch (err) {
                    const { generic, fields } = formatResponseError(err);
                    this.errorToast({
                        text: generic || `Could not ${this.isPublished ? 'unpublish' : 'publish'} owner statement.`,
                    });
                } finally {
                    this.loading = false;
                    this.$bvModal.hide(this.publishModalId);
                }
            },
            async regenerate() {
                this.loading = true;
                try {
                    if (this.isOwnerStatement) {
                        const { data } = await API.post(OWNER_STATEMENT_ENDPOINT(this.id) + 'regenerate/');
                        this.params.onRegenerateSuccess(data);
                    } else {
                        const { data } = await API.post(OWNER_STATEMENT_GENERATION_ENDPOINT(this.id) + 'regenerate/', {
                            owners: this.params.ownersRequest,
                            properties: this.params.propertiesRequest,
                            show_empty_statements: this.params.showEmptyStatements,
                        });
                        this.params.onRegenerateSuccess(this.statementPostfix, data);
                    }
                } catch (err) {
                    const { generic, fields } = formatResponseError(err);
                    this.errorToast({
                        text: generic || `Could not regenerate owner statement${this.statementPostfix}.`,
                    });
                } finally {
                    this.loading = false;
                    this.$bvModal.hide(this.regenerateModalId);
                }
            },
            handleViewClick() {
                this.$router.push(`/app/owner-statement/list/${this.id}/preview`);
            },
            handleLogsClick() {
                this.$router.push(`/app/owner-statement/list/logs?generation_id=${this.id}`);
            },
        },
    };
</script>
